import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * ShareReceiver — local Capacitor plugin for the native share sheet.
 *
 * A *dedicated* channel for content shared into Enough from other apps, kept entirely
 * separate from `appUrlOpen` deep-link handling (see
 * `docs/future_product_specs/CART_COMPANION_SHARE_SHEET.md`).
 *
 * - iOS: a Share Extension "mini-app" lets the user set price + duration, then appends
 *   `{url, price, periodDays, sharedAt}` to a queue in the shared App Group. Multiple
 *   items survive without opening the app; the host app drains them on next foreground.
 * - Android: a SEND intent's `EXTRA_TEXT` is captured in MainActivity (URL only — the
 *   app's in-app preview handles price/duration since Android opens the app directly).
 */
export interface PendingShareItem {
  /** The shared URL/text. */
  url: string;
  /** Price the user typed in the share extension (iOS), as a string. */
  price?: string;
  /** Cooling-off duration in days chosen in the extension (iOS). Absent → use in-app preview. */
  periodDays?: number;
  /** ISO timestamp of when the user shared (the hold counts from here). */
  sharedAt?: string;
}

export interface ShareReceiverPlugin {
  /**
   * Return all pending shared items and CLEAR the queue. Safe to call repeatedly —
   * returns `{ items: [] }` once drained. Call on app launch and on resume.
   */
  getPendingShares(): Promise<{ items: PendingShareItem[] }>;

  /**
   * Store the signed-in user's Firebase refresh token in the shared Keychain (iOS) so the
   * Share Extension can mint ID tokens and write directly to Firestore. Pass an empty
   * token to clear. No-op on Android (the SEND intent opens the app directly).
   */
  setShareCredentials(options: { refreshToken: string }): Promise<void>;

  /** Clear the shared credential (call on logout / anonymous). */
  clearShareCredentials(): Promise<void>;

  /** Fired when content is shared while the app is already running (Android). */
  addListener(
    eventName: 'shareReceived',
    listenerFunc: (item: PendingShareItem) => void,
  ): Promise<PluginListenerHandle>;

  removeAllListeners(): Promise<void>;
}

const ShareReceiver = registerPlugin<ShareReceiverPlugin>('ShareReceiver', {
  web: () => ({
    getPendingShares: async () => ({ items: [] }),
    setShareCredentials: async () => {},
    clearShareCredentials: async () => {},
    addListener: async () => ({ remove: async () => {} }) as PluginListenerHandle,
    removeAllListeners: async () => {},
  }),
});

export default ShareReceiver;
